import React, {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import { noop } from '@adsk/alloy-react-helpers';
import {
  CheckmarkIcon,
  ChevronRightIcon,
  Icon,
  RenderIconType,
} from '@adsk/alloy-react-icon';
import { useTheme, MenuBackgrounds } from '@adsk/alloy-react-styles';
import { StylableComponent, stylePropType } from '@adsk/alloy-react-theme';

import { useMenuContext } from './MenuContext';
import { StyledButton } from './StyledButton';

type MenuItemProps = StylableComponent<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
> &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'style' | 'css' | 'onClick'> & {
    'data-testid'?: string;
    /** Icon renderer displayed to the left of the label */
    renderIcon?: RenderIconType;
    /** The button text */
    label?: ReactNode;
    /** A text on the right side of the item */
    rightSideLabel?: ReactNode;
    /** Determines if the item is selected and shows a checkmark next to it */
    selected?: boolean;
    /** Determines if the checkmark icon area is reserved */
    showSelection?: boolean;
    /** Applies the hover background as the resting state */
    highlighted?: boolean;
    /** Renders a chevron and enables submenu behavior */
    nested?: boolean;
    onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
    onFocus?: React.FocusEventHandler<HTMLButtonElement>;
    /**
     * Override default background colors for specific menu item states.
     * Only the keys you provide are overridden; the rest fall back to the theme tokens.
     *
     * @example
     * ```tsx
     * <MenuItem
     *   overrides={{
     *     backgrounds: {
     *       idle: '#fff',
     *       hover: '#f0f0f0',
     *       active: '#e0e0e0',
     *       selected: '#d0d0ff',
     *       selectedHover: '#c0c0ff',
     *       selectedActive: '#b0b0ff',
     *     },
     *   }}
     * />
     * ```
     */
    overrides?: { backgrounds?: Partial<MenuBackgrounds> };
  };

/** A MenuItem component which wraps an HTML li containing other children */
const MenuItem = React.forwardRef<HTMLButtonElement, MenuItemProps>(
  (
    {
      children,
      label,
      rightSideLabel,
      selected = false,
      disabled = false,
      renderIcon,
      className,
      showSelection = true,
      nested = false,
      highlighted,
      onClick,
      style,
      onKeyDown,
      onKeyUp,
      onBlur,
      overrides,
      onMouseEnter: onMouseEnterProp,
      onFocus: onFocusProp,
      ...props
    },
    ref,
  ) => {
    const [isEnterActive, setIsEnterActive] = useState(false);
    const menuContext = useMenuContext();
    const dynamicTheme = useTheme();
    const menuTokens = dynamicTheme.tokens?.component?.menu;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (['Enter', ' '].includes(e.key) && !disabled) {
        setIsEnterActive(true);
      }
      onKeyDown?.(e);
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (['Enter', ' '].includes(e.key)) {
        setIsEnterActive(false);
      }
      onKeyUp?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
      setIsEnterActive(false);
      onBlur?.(e);
    };

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) {
        return;
      }
      onMouseEnterProp?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
      onFocusProp?.(e);
    };

    return (
      <StyledButton
        ref={ref}
        as={nested ? 'div' : undefined}
        className={className}
        disabled={disabled}
        role={'menuitem'}
        aria-disabled={disabled ? true : undefined}
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? noop : onClick}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onBlur={handleBlur}
        onMouseEnter={handleMouseEnter}
        onFocus={handleFocus}
        $showSelection={showSelection}
        $selected={selected}
        $highlighted={highlighted}
        $nested={nested}
        $tokens={dynamicTheme.tokens}
        $overrideBackgrounds={overrides?.backgrounds}
        $isKeyboardNavigation={menuContext?.isKeyboardNavigation}
        $isEnterActive={isEnterActive}
        data-keyboard-navigation={
          menuContext?.isKeyboardNavigation ? 'true' : 'false'
        }
        data-enter-active={isEnterActive}
        css={style}
        {...props}
      >
        {showSelection &&
          (selected ? (
            <CheckmarkIcon
              style={{
                flex: '0 0 22',
                color: menuTokens?.item?.icon?.selected.color,
                marginRight: menuTokens?.item?.icon._?.marginRight,
                flexShrink: 0,
              }}
            />
          ) : (
            /* placeholder for styling, no icon displayed */
            <Icon
              style={{
                flex: '0 0 22',
                marginRight: menuTokens?.item?.icon._?.marginRight,
                flexShrink: 0,
              }}
            />
          ))}
        {renderIcon?.({
          style: {
            marginRight: menuTokens?.item?.icon._?.marginRight,
            flexShrink: 0,
          },
        })}
        {label && (
          <span
            dir="auto"
            css={[
              dynamicTheme.tokens?.component?.typography?.ellipsis,
              { flex: '1 1 auto', minWidth: 0 },
            ]}
          >
            {label}
          </span>
        )}

        {children}

        {(rightSideLabel || nested) && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: 'auto',
              flexShrink: 0,
              gap: menuTokens?.item?.rightSide?.gap,
            }}
          >
            {rightSideLabel && (
              <span
                dir="auto"
                css={[
                  dynamicTheme.tokens?.component.typography.bodySmall,
                  menuTokens?.item?.icon.idle,
                ]}
              >
                {rightSideLabel}
              </span>
            )}
            {nested && (
              <ChevronRightIcon style={{ ...menuTokens?.item?.icon.idle }} />
            )}
          </span>
        )}
      </StyledButton>
    );
  },
);

MenuItem.displayName = 'MenuItem';

MenuItem.propTypes = {
  /** The content of the menu item */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Determines if the item is selected and shows a checkmark next to it */
  selected: PropTypes.bool,
  /** Determines if the checkmark icon area is reserved */
  showSelection: PropTypes.bool,
  /** Applies the hover background as the resting state */
  highlighted: PropTypes.bool,
  /** Renders a chevron and enables submenu behavior */
  nested: PropTypes.bool,
  /** Determines if the item is disabled */
  disabled: PropTypes.bool,
  /** The button text */
  label: PropTypes.any,
  /** A text on the right side of the item */
  rightSideLabel: PropTypes.any,
  /** Icon renderer displayed to the left of the label */
  renderIcon: PropTypes.func,
  /** Handle for item click event */
  onClick: PropTypes.func,
  /** Override default background colors for specific menu item states */
  overrides: PropTypes.shape({
    backgrounds: PropTypes.shape({
      idle: PropTypes.string,
      hover: PropTypes.string,
      active: PropTypes.string,
      selected: PropTypes.string,
      selectedHover: PropTypes.string,
      selectedActive: PropTypes.string,
    }),
  }),
};
export default MenuItem;
